<template>
    <Form ref="signalForm" class="form" :model="localData" label-position="right" inline>
        <FormItem label="which triggers on">
            <DatePicker
                v-model="localData.startDate"
                type="date"
                placeholder="Start date"
                data-testid="signal-modal__start-date"
            />
        </FormItem>

        <FormItem label="at">
            <TimePicker
                v-model="localData.startTime"
                format="HH:mm"
                placeholder="Start time"
                data-testid="signal-modal__start-time"
            />
        </FormItem>

        <FormItem label="and ends on">
            <DatePicker
                v-model="localData.endDate"
                type="date"
                placeholder="End date"
                data-testid="signal-modal__end-date"
            />
        </FormItem>

        <FormItem label="at">
            <TimePicker
                v-model="localData.endTime"
                format="HH:mm"
                placeholder="End time"
                data-testid="signal-modal__end-time"
            />
        </FormItem>

        <FormItem label="across these days">
            <Dropdown placement="bottom-start" trigger="click" data-testid="signal-modal__weekdays-dropdown">
                <hox-dropdown-button data-testid="library__visibility-select">Select days</hox-dropdown-button>

                <template #list>
                    <CheckboxGroup v-model="localData.byweekday" vertical>
                        <Checkbox v-for="weekDay in WEEK_DAYS" :key="weekDay.value" :label="weekDay.value">
                            {{ weekDay.label }}
                        </Checkbox>
                    </CheckboxGroup>
                </template>
            </Dropdown>
        </FormItem>

        <FormItem label="in this timezone">
            <Select
                ref="tzid"
                v-model="localData.tzid"
                placeholder="Select Timezone"
                prop="tzid"
                data-testid="signal-modal__tzid"
            >
                <Option v-for="timezone in TIMEZONES" :key="timezone.label" :value="timezone.value">
                    {{ timezone.label }}
                </Option>
            </Select>
        </FormItem>
    </Form>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { RRule, rrulestr } from "rrule";
import { set } from "date-fns/set";
import { isAfter } from "date-fns/isAfter";

import { WEEK_DAYS, TIMEZONES } from "@/Connect/consts";
import { snackbar } from "@/utils";

function getDate(startDate: Date, startTime: string) {
    const [hours, minutes] = startTime.split(":");

    return set(startDate, {
        hours: Number(hours),
        minutes: Number(minutes)
    });
}

function showEndDateError() {
    snackbar.error("The start date is after the end date. Please check the dates and times.");
}

interface DatetimeSignalForm {
    startDate: Date;
    endDate?: Date;
    startTime: string;
    endTime: string;
    byweekday: number[];
    tzid: string;
}

const DEFAULT_VALUES = {
    startDate: new Date(),
    startTime: "00:00",
    byweekday: [0, 1, 2, 3, 4, 5, 6]
} as DatetimeSignalForm;

const props = defineProps<{
    modelValue: string;
}>();

const emit = defineEmits<(e: "update:modelValue", value: string) => void>();

const localData = ref<DatetimeSignalForm>({ ...DEFAULT_VALUES });

watch(
    () => props.modelValue,
    (newVal, oldVal) => {
        if (!newVal) {
            return;
        }

        if (newVal === oldVal) {
            return;
        }

        try {
            const rrule = rrulestr(newVal);

            localData.value = { ...DEFAULT_VALUES };

            localData.value.startDate = rrule.options.dtstart;
            localData.value.startTime = rrule.options.dtstart.toTimeString().slice(0, 5);

            localData.value.endDate = rrule.options.until ?? undefined;
            localData.value.endTime = rrule.options.until?.toTimeString().slice(0, 5) ?? "";

            localData.value.byweekday = rrule.options.byweekday;
            localData.value.tzid = rrule.options.tzid ?? "";
        } catch (error) {
            console.error("error", error);
        }
    },
    { immediate: true }
);

watch(
    localData,
    (newVal: DatetimeSignalForm) => {
        if (newVal.startDate && !newVal.startTime) {
            localData.value.startTime = "00:00";

            // setting this will re-trigger the watch
            return;
        }

        if (newVal.endDate && !newVal.endTime) {
            localData.value.endTime = "00:00";

            // setting this will re-trigger the watch
            return;
        }

        const startDate = getDate(newVal.startDate, newVal.startTime);
        const endDate = newVal.endDate ? getDate(newVal.endDate, newVal.endTime) : undefined;

        if (endDate && isAfter(startDate, endDate)) {
            // TODO: add proper form validation
            showEndDateError();

            return;
        }

        const rruleOptions = {
            dtstart: startDate,
            ...(endDate ? { until: endDate } : {}),
            freq: 3,
            byweekday: newVal.byweekday ?? [],
            tzid: newVal.tzid
        };

        emit("update:modelValue", RRule.optionsToString(rruleOptions));
    },
    { immediate: true, deep: true }
);
</script>

<style scoped>
.form {
    display: flex;
    flex-wrap: wrap;
}

.form > .ivu-form-item {
    padding: 10px 0;
    margin-bottom: 0;

    display: flex;
}
</style>
